import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit } from '@angular/core';
import { Employee } from 'src/app/modules/shared/models/employee';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { EmployeeDetailsViewModel } from 'src/app/modules/shared/viewModels/employeeDetailsViewModel';
import { Account } from 'src/app/modules/shared/models/account';
import { DepartmentVisibilities } from 'src/app/modules/shared/enums/departmentVisibility.enum';
import { ModuleTypes } from 'src/app/modules/settings/enums/moduleTypes';
import { FilterTypes } from 'src/app/modules/shared/enums/filterTypes';
import { EmployeeRoleTypes } from 'src/app/modules/shared/enums/employees/EmployeeRoleTypes';
import { PermissionViewModel } from 'src/app/modules/shared/viewModels/permissionViewModel';
import { EmployeeUtil } from 'src/app/modules/shared/utilities/employee.utilities';
import { PrivacyStatuses } from '../../../../../../shared/enums/privacyStatuses';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { FilterViewModel } from 'src/app/modules/shared/models/filter/filterViewModel';
import { FilterUtilities } from 'src/app/modules/shared/utilities/filter.utilities';
import { T } from 'src/assets/i18n/translation-keys';
import { TranslateService } from '@ngx-translate/core';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { PermissionHelper } from 'src/app/modules/settings/utils/permission.helper';
import { filter } from 'rxjs';

enum ReadWriteTypes {
  read = 'read',
  write = 'write',
}

@Component({
    selector: 'app-control-employee-permissions',
    templateUrl: './control-employee-permissions.component.html',
    styleUrls: ['./control-employee-permissions.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ControlEmployeePermissionsComponent implements OnInit, OnChanges {
  @Input() employeeDetailsViewModel: EmployeeDetailsViewModel;
  @Input() isInPrimaryRole: boolean;
  @Input() isAccountAdmin: boolean;
  @Input() isDepartmentSelected: boolean;

  public account: Account;
  private employee: Employee;

  public readIncidentsDepVisibility: DepartmentVisibilities;
  public writeIncidentDepVisibility: DepartmentVisibilities;
  public readJobsDepVisibility: DepartmentVisibilities;
  public writeJobsDepVisibility: DepartmentVisibilities;

  public readonly employeeRoleTypes = EmployeeRoleTypes;
  public readonly filterTypes = FilterTypes;
  public readonly departmentVisibilites = DepartmentVisibilities;
  public readonly moduleTypes = ModuleTypes;
  public readonly readWriteTypes = ReadWriteTypes;

  confidentialJobsTitle = 'Confidential Jobs';
  public privacyStatus = PrivacyStatuses;
  public localisePEAPs: string;
  public localisedDepartment: string = 'Department';
  public localisedDepartments: string = 'Departments';
  public confidentialTypeFilters: FilterViewModel[] = [];
  public confidentialJobTypeFilters: FilterViewModel[] = [];
  public privacyStatusFilters: FilterViewModel[] = [];
  public readonly T = T;
  public localisedEmployee = 'Employee';
  public localisedJobs = 'Jobs';
  public localisedIncidents = 'Incidents';
  public localisedLogs = 'Logs';
  public objectTypes = ObjectTypes;
  localisedIncidentsAndLogs: string;

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly localisationService: LocalisationService,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.employee = this.authenticationService.getCurrentEmployee();
    this.account = this.authenticationService.getCurrentAccount();
    this.initLocalisations();
    this.initDepartmentVisibilities();
    this.initConfidentialTypeFilters();
  }

  ngOnChanges() {
    this.initDepartmentVisibilities();
  }

  initLocalisations() {
    this.localisePEAPs = this.localisationService.localiseObjectType(ObjectTypes.PEAP, true);
    this.localisedDepartment = this.localisationService.localiseObjectType(ObjectTypes.Department);
    this.localisedDepartments = this.localisationService.localiseObjectType(ObjectTypes.Department, true);
    this.localisedJobs = this.localisationService.localiseObjectType(ObjectTypes.Job, true);
    this.confidentialJobsTitle = this.translateService.instant(T.common.confidential_items, { items: this.localisedJobs });
    this.localisedEmployee = this.localisationService.localiseObjectType(ObjectTypes.Employee);
    this.localisedIncidents = this.localisationService.localiseObjectType(ObjectTypes.IncidentItem, true);
    this.localisedLogs = this.translateService.instant(T.defaultLocalizations.log.many);
    this.localisedIncidentsAndLogs = this.localisedIncidents + ' & ' + this.localisedLogs;
    const privacyStatuses = this.localisationService.getLocalizedIncidentPrivacyStatuses();
    privacyStatuses.forEach((status) => {
      this.privacyStatusFilters.push(FilterUtilities.GenerateFilter(FilterTypes.Privacy_Status, status.key, status.value));
    });
  }

  initConfidentialTypeFilters() {
    this.employeeDetailsViewModel.permissions.forEach((p) => {
      if (p.module == ModuleTypes.Incidents && p.type == FilterTypes.Incident && p.confidential)
        this.confidentialTypeFilters.push(this.privacyStatusFilters.find((f) => f.filterValue.toString() == p.level.toString()));
    });

    this.initJobConfidentialTypeFilters();
  }

  initJobConfidentialTypeFilters() {
    this.employeeDetailsViewModel.permissions.forEach((p) => {
      if (p.module == ModuleTypes.Incidents && p.type == FilterTypes.Job && p.confidential)
        this.confidentialJobTypeFilters.push(
          this.privacyStatusFilters.find((f) => f.filterValue.toString() == p.level.toString())
        );
    });
  }

  selectIncidentUserRole(isAdd: boolean) {
    if (isAdd) {
      this.employeeDetailsViewModel.modulePermissionViewModels.push({
        employeeId: this.employeeDetailsViewModel.id,
        id: 0,
        module: ModuleTypes.Incidents,
        objectType: ObjectTypes.IncidentItem,
        createdById: this.employee.id,
        created: '',
        accountId: this.employee.accountId,
      });
      // Here we need to ADD role that reponds to the Can Access PEAPs but not same like PEAPS CONTROLLER
      this.selectPeapsAccess(true);
      this.readIncidentsDepVisibility = DepartmentVisibilities.All_Departments;
      this.writeIncidentDepVisibility = DepartmentVisibilities.All_Departments;
      this.employeeDetailsViewModel.permissions.push(
        PermissionHelper.generatePermission(this.employeeDetailsViewModel.id, this.employee.accountId, this.employee.id, ModuleTypes.Incidents, FilterTypes.Incident, 0, this.readIncidentsDepVisibility, ReadWriteTypes.read, false)
      );
      this.employeeDetailsViewModel.permissions.push(
        PermissionHelper.generatePermission(this.employeeDetailsViewModel.id, this.employee.accountId, this.employee.id, ModuleTypes.Incidents, FilterTypes.Incident, 0, this.writeIncidentDepVisibility, ReadWriteTypes.write, false)
      );
    } else {
      this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
        (p) => p.module != ModuleTypes.Incidents
      );
      this.employeeDetailsViewModel.modulePermissionViewModels = this.employeeDetailsViewModel.modulePermissionViewModels.filter(
        (r) => r.module != ModuleTypes.Incidents
      );
      this.selectPeapsAccess(false);
      // Also we need to remove it when deselecting ****
    }
    this.initDepartmentVisibilities();
    this.changeDetectorRef.detectChanges();
  }

  selecUserRole(isAdd: boolean, objectType: ObjectTypes) {
    const filterType = objectType == ObjectTypes.Job ? FilterTypes.Job : FilterTypes.Incident;

    if (isAdd) {
      this.employeeDetailsViewModel.modulePermissionViewModels.push({
        employeeId: this.employeeDetailsViewModel.id,
        id: 0,
        module: ModuleTypes.Incidents,
        objectType: objectType,
        createdById: this.employee.id,
        created: '',
        accountId: this.employee.accountId,
      });

      this.readJobsDepVisibility = DepartmentVisibilities.All_Departments;
      this.writeJobsDepVisibility = DepartmentVisibilities.All_Departments;

      this.employeeDetailsViewModel.permissions.push(
        PermissionHelper.generatePermission(this.employeeDetailsViewModel.id, this.employee.accountId, this.employee.id, ModuleTypes.Incidents, filterType, 0, this.readJobsDepVisibility, ReadWriteTypes.read, false)
      );
      this.employeeDetailsViewModel.permissions.push(
        PermissionHelper.generatePermission(this.employeeDetailsViewModel.id, this.employee.accountId, this.employee.id, ModuleTypes.Incidents, filterType, 0, this.readJobsDepVisibility, ReadWriteTypes.write, false)
      );
    } else {
      this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
        (p) => !(p.module == ModuleTypes.Incidents && p.type == filterType)
      );
      this.employeeDetailsViewModel.modulePermissionViewModels = this.employeeDetailsViewModel.modulePermissionViewModels.filter(
        (r) => r.objectType != objectType
      );
    }
    this.initDepartmentVisibilities();

    if(objectType == ObjectTypes.IncidentItem && !isAdd && this.isInRole(this.employeeRoleTypes.Control_Admin)) {
      this.employeeDetailsViewModel.employeeRole = this.employeeDetailsViewModel.employeeRole.filter(
        (r) => r.roleId != EmployeeRoleTypes.Control_Admin
      );
    }

    this.changeDetectorRef.detectChanges();
  }

  selectControlAdmin(isAdd: boolean) {
    if (isAdd) {
      this.employeeDetailsViewModel.employeeRole.push({
        employeeId: this.employeeDetailsViewModel.id,
        id: 0,
        roleId: EmployeeRoleTypes.Control_Admin,
        created: '',
        accountId: 0,
      });
    } else {
      this.employeeDetailsViewModel.employeeRole = this.employeeDetailsViewModel.employeeRole.filter(
        (r) => r.roleId != EmployeeRoleTypes.Control_Admin
      );
    }
    this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
      (p) => !(p.module == ModuleTypes.Incidents && p.type == FilterTypes.Incident && !p.confidential)
    );
    this.employeeDetailsViewModel.permissions.push(
      PermissionHelper.generatePermission(this.employeeDetailsViewModel.id, this.employee.accountId, this.employee.id, ModuleTypes.Incidents, FilterTypes.Incident, 0, DepartmentVisibilities.All_Departments, ReadWriteTypes.read, false)
    );
    this.employeeDetailsViewModel.permissions.push(
      PermissionHelper.generatePermission(this.employeeDetailsViewModel.id, this.employee.accountId, this.employee.id, ModuleTypes.Incidents, FilterTypes.Incident, 0, DepartmentVisibilities.All_Departments, ReadWriteTypes.write, false)
    );
    this.initDepartmentVisibilities();
  }

  selectPeapsAccess(isAdd: boolean) {
    if (isAdd) {
      this.employeeDetailsViewModel.modulePermissionViewModels.push({
        employeeId: this.employeeDetailsViewModel.id,
        id: 0,
        module: ModuleTypes.Incidents,
        objectType: ObjectTypes.PEAP,
        createdById: this.employee.id,
        created: '',
        accountId: this.employee.accountId,
      });
    } else {
      this.employeeDetailsViewModel.modulePermissionViewModels = this.employeeDetailsViewModel.modulePermissionViewModels.filter(
        (r) => r.objectType != ObjectTypes.PEAP
      );
      this.employeeDetailsViewModel.employeeRole = this.employeeDetailsViewModel.employeeRole.filter(
        (r) => r.roleId != EmployeeRoleTypes.PEAPS_Controller
      );
    }
  }

  selectPeapsController(isAdd: boolean) {
    if (isAdd) {
      this.employeeDetailsViewModel.employeeRole.push({
        employeeId: this.employeeDetailsViewModel.id,
        id: 0,
        roleId: EmployeeRoleTypes.PEAPS_Controller,
        created: '',
        accountId: 0,
      });
      if (!EmployeeUtil.hasAccessToObjectType(this.employeeDetailsViewModel, ObjectTypes.PEAP)) {
        this.employeeDetailsViewModel.modulePermissionViewModels.push({
        employeeId: this.employeeDetailsViewModel.id,
        id: 0,
        module: ModuleTypes.Incidents,
        objectType: ObjectTypes.PEAP,
        createdById: this.employee.id,
        created: '',
        accountId: this.employee.accountId,
      });
      }
    } else {
      this.employeeDetailsViewModel.employeeRole = this.employeeDetailsViewModel.employeeRole.filter(
        (r) => r.roleId != EmployeeRoleTypes.PEAPS_Controller
      );
    }
  }

  private initDepartmentVisibilities() {
    this.readIncidentsDepVisibility = this.getDepartmentVisibility(ReadWriteTypes.read, FilterTypes.Incident);
    this.writeIncidentDepVisibility = this.getDepartmentVisibility(ReadWriteTypes.write, FilterTypes.Incident);

    this.readJobsDepVisibility = this.getDepartmentVisibility(ReadWriteTypes.read, FilterTypes.Job);
    this.writeJobsDepVisibility = this.getDepartmentVisibility(ReadWriteTypes.write, FilterTypes.Job);
  }

  isInRole(employeeRole: EmployeeRoleTypes) {
    return EmployeeUtil.hasRole(this.employeeDetailsViewModel, employeeRole);
  }

  hasAccessToObject(objectType: ObjectTypes) {
    return EmployeeUtil.hasAccessToObjectType(this.employeeDetailsViewModel, objectType);
  }

  hasAccessToModule(module: ModuleTypes) {
    return EmployeeUtil.hasAccessToModule(this.employeeDetailsViewModel, module);
  }

  getDepartmentVisibility(readOrWrite: ReadWriteTypes, filterType: FilterTypes, confidential = false) {
    return PermissionHelper.getDepartmentVisibility(this.employeeDetailsViewModel, readOrWrite, filterType, ModuleTypes.Risk, confidential);
  }

  changeDepartmentVisibility(
    currentDepVisibility: DepartmentVisibilities,
    filterType: FilterTypes,
    readOrWrite: ReadWriteTypes,
    confidential = false
  ) {

    PermissionHelper.changeDepartmentVisibility(this.employeeDetailsViewModel, currentDepVisibility, ModuleTypes.Incidents, filterType, readOrWrite, confidential);
    this.initDepartmentVisibilities();
  }

  hasWritePermission(type: FilterTypes, values: number[], confidential: boolean): boolean {
    return PermissionHelper.hasWritePermission(this.employeeDetailsViewModel, this.isInPrimaryRole, ModuleTypes.Incidents, type, values, confidential);
  }

  updateConfidentialTypes(filters: FilterViewModel[]) {
    this.confidentialTypeFilters = filters;
    this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
      (p) => !(p.module == ModuleTypes.Incidents && p.type == FilterTypes.Incident && p.confidential)
    );
    filters.forEach((f) => {
      this.employeeDetailsViewModel.permissions.push(
        PermissionHelper.generatePermission(this.employeeDetailsViewModel.id, this.employee.accountId, this.employee.id, ModuleTypes.Incidents,
          FilterTypes.Incident,
          0,
          DepartmentVisibilities.All_Departments,
          this.readWriteTypes.write,
          true,
          f.filterValue
        )
      );
    });
  }

  updateJobConfidentialTypes(filters: FilterViewModel[]) {
    this.confidentialJobTypeFilters = filters;
    this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
      (p) => !(p.module == ModuleTypes.Incidents && p.type == FilterTypes.Job && p.confidential)
    );
    filters.forEach((f) => {
      this.employeeDetailsViewModel.permissions.push(
        PermissionHelper.generatePermission(this.employeeDetailsViewModel.id, this.employee.accountId, this.employee.id, ModuleTypes.Incidents,
          FilterTypes.Job,
          0,
          DepartmentVisibilities.All_Departments,
          this.readWriteTypes.write,
          true,
          f.filterValue
        )
      );
    });
  }

  hasConfidentialPermission(level: number) {
    return this.employeeDetailsViewModel.permissions.some(
      (p) => p.module == ModuleTypes.Incidents && p.type == FilterTypes.Incident && p.confidential && p.level == level
    );
  }

  setConfidentialLevelJobPermission(isAdd: boolean, level: number) {
    if (isAdd) {
      this.employeeDetailsViewModel.permissions.push(
        PermissionHelper.generatePermission(this.employeeDetailsViewModel.id, this.employee.accountId, this.employee.id, ModuleTypes.Incidents,
          FilterTypes.Job,
          0,
          DepartmentVisibilities.All_Departments,
          this.readWriteTypes.write,
          true,
          level
        )
      );
    } else {
      this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
        (p) => !(p.module == ModuleTypes.Incidents && p.type == FilterTypes.Job && p.confidential && p.level == level)
      );
    }
  }

  localisePrivacyStatus(status: PrivacyStatuses) {
    return this.localisationService.localiseFilterValueByFilterType(status, FilterTypes.Privacy_Status);
  }

  updateEmployeePermissions(permissions: PermissionViewModel[]) {
    this.employeeDetailsViewModel.permissions = permissions.slice();
    this.changeDetectorRef.markForCheck();
  }
}
